import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

export default class extends BridgeComponent {
  static component = "navigation-guard"

  // The browser confirmation protects web edits too; only bridge messages need
  // native support.
  static get shouldLoad() { return true }

  static targets = ["form"]

  static values = {
    id: { type: String, default: "form" },
    title: { type: String, default: "Discard changes?" },
    message: { type: String, default: "Your changes haven't been saved." },
    confirmTitle: { type: String, default: "Discard" },
    cancelTitle: { type: String, default: "Keep editing" }
  }

  connect() {
    super.connect()

    this.guardedForm = this.hasFormTarget ? this.formTarget : this.element
    this.initialState = this.formState
    this.active = false
    this.submitting = false

    this.form.addEventListener("input", this.update)
    this.form.addEventListener("change", this.update)
    this.form.addEventListener("turbo:submit-start", this.submitStarted)
    this.form.addEventListener("turbo:submit-end", this.submitEnded)
    document.addEventListener("turbo:before-visit", this.beforeVisit)
    window.addEventListener("beforeunload", this.beforeUnload)
    this.formObserver = new MutationObserver(this.update)
    this.formObserver.observe(this.form, { childList: true, subtree: true })

    this.sendState("connect")

    // Alpine can fill in bound field values after this controller connects.
    // Re-take the baseline once they have settled so they don't count as edits.
    this.baselineTimeout = setTimeout(() => {
      if (!this.active) this.initialState = this.formState
    }, 0)
  }

  disconnect() {
    clearTimeout(this.baselineTimeout)
    this.form.removeEventListener("input", this.update)
    this.form.removeEventListener("change", this.update)
    this.form.removeEventListener("turbo:submit-start", this.submitStarted)
    this.form.removeEventListener("turbo:submit-end", this.submitEnded)
    document.removeEventListener("turbo:before-visit", this.beforeVisit)
    window.removeEventListener("beforeunload", this.beforeUnload)
    this.formObserver.disconnect()

    this.send("disconnect", { id: this.idValue }, () => {})
    super.disconnect()
  }

  update = () => {
    this.setActive(!this.submitting && this.formState !== this.initialState)
  }

  deactivate() {
    this.initialState = this.formState
    this.setActive(false)
  }

  submitStarted = () => {
    // Turbo can navigate after saving before turbo:submit-end fires.
    // Suspend the guard without forgetting edits in case the request fails.
    this.submitting = true
    this.setActive(false)
  }

  submitEnded = (event) => {
    this.submitting = false
    if (event.detail.success) this.initialState = this.formState

    this.update()
  }

  beforeVisit = (event) => {
    if (this.active && !window.confirm(`${this.titleValue}\n\n${this.messageValue}`)) {
      event.preventDefault()
    }
  }

  beforeUnload = (event) => {
    if (!this.active) return

    event.preventDefault()
    event.returnValue = ""
  }

  setActive(active) {
    if (this.active === active) return

    this.active = active
    this.sendState("update")
  }

  sendState(event) {
    this.send(event, {
      id: this.idValue,
      active: this.active,
      title: this.titleValue,
      message: this.messageValue,
      confirmTitle: this.confirmTitleValue,
      cancelTitle: this.cancelTitleValue
    }, () => {})
  }

  get formState() {
    // FormData gives an unselected file input a fresh lastModified timestamp.
    // Only compare timestamps for files the user actually selected.
    const fields = Array.from(new FormData(this.form), ([name, value]) => [
      name,
      value instanceof File ? [value.name, value.size, value.name ? value.lastModified : null] : value
    ])

    return JSON.stringify(fields)
  }

  get form() {
    return this.guardedForm
  }
}
